import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../utils/axiosDash";
import { Modal, Input } from "antd";

const STORE_STORAGE_KEY = "selectedStore";

/* ---------- API ---------- */
const apiError = (error, fallback) => {
  if (error.response) {
    return new Error(
      error.response.data?.message ||
        error.response.data?.msg ||
        `${fallback} Status: ${error.response.status}`
    );
  }
  if (error.request) return new Error("No response received from the server.");
  return new Error(error.message || fallback);
};

// Fetch images for ONE store / restaurant name (or whatever name is passed in)
async function fetchImages(storeName) {
  try {
    const { data } = await axios.post("/fetch-empire-images", {
      storeName,
    });

    const items = Array.isArray(data.data)
      ? data.data.map((item) => ({
          ...item,

          // Normalize backend camelCase fields
          camera_ip: item.cameraIp,
          camera_id: item.channel,
          image_url: item.imageUrl || "",
        }))
      : [];

    return {
      store: data.store || storeName,
      items,
    };
  } catch (error) {
    console.error("Error while fetching images:", error);
    throw apiError(error, "Failed to fetch images.");
  }
}

// List of restaurant names for the "All Stores" view (no images are kept from here).
// 1) tries a lightweight stores endpoint
// 2) falls back to one "All Stores" fetch and keeps only the unique names
// List of restaurant names for the "All Stores" view (names only, no images)
async function fetchRestaurants() {
  try {
    const { data } = await axios.post("/fetch-empire-stores", {});
    const list = Array.isArray(data?.data) ? data.data : [];
    return [...new Set(list.map((n) => String(n || "").trim()).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b, undefined, { numeric: true })
    );
  } catch (error) {
    console.error("Error while fetching restaurants:", error);
    throw apiError(error, "Failed to fetch restaurants.");
  }
}

async function saveRoi(imageId, payload) {
  try {
    const { data } = await axios.post("/save-empire-roi", { imageId, ...payload });
    return data;
  } catch (error) {
    console.error("Error while saving ROI:", error);
    throw apiError(error, "Failed to save ROI.");
  }
}

async function fetchEmpireRoi(storeName) {
  try {
    const { data } = await axios.post("/fetch-empire-roi", {
      store: storeName,
    });

    return Array.isArray(data.data) ? data.data : [];
  } catch (error) {
    // 404 means the store simply doesn't have ROI yet
    if (error.response?.status === 404) {
      return [];
    }

    console.error("Error while fetching Empire ROI:", error);
    throw apiError(error, "Failed to fetch Empire ROI.");
  }
}

/* ---------- constants & helpers ---------- */
const COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6"];
const MIN_SIZE = 0.012;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const NO_IP = "Unknown IP";
const NO_STORE = "Unknown restaurant";
const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const uid = () => crypto.randomUUID();

// recompute bounding box (normalized) from polygon points
const withBBox = (points) => {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { points, x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
};

// normalized area of a region (rectangle or polygon)
const regionArea = (r) =>
  r.type === "polygon"
    ? Math.abs(
        r.points.reduce((s, p, i) => {
          const q = r.points[(i + 1) % r.points.length];
          return s + p.x * q.y - q.x * p.y;
        }, 0)
      ) / 2
    : r.w * r.h;

const svgPoints = (pts) => pts.map((p) => `${p.x * 100},${p.y * 100}`).join(" ");

// true when the admin selected "All Stores"
const isAllStoresName = (name) => {
  const n = String(name || "").trim().toLowerCase().replace(/\s+/g, " ");
  return n === "all stores" || n === "all";
};

// camera IP of an item (falls back to a label so items without an IP are still reachable)
const ipOf = (it) => (
  it.cameraIp
    ? String(it.cameraIp).trim()
    : NO_IP
);

// restaurant / store name of an item.
// NOTE: adjust the field names here if your backend uses a different key.
const storeOf = (it) => {
  const v = it.storeName || it.store || it.restaurantName || it.restaurant || it.outletName;
  return v ? String(v).trim() : NO_STORE;
};

const sortIps = (list) =>
  [...new Set(list.map(ipOf))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

// find the saved ROI document that belongs to an image (same camera IP + channel [+ restaurant])
const matchRoi = (roiList, item) => {
  if (!item) return null;

  const cameraIp = item.cameraIp || item.camera_ip;
  const channel = String(item.channel || item.camera_id || "");
  const itemStore = storeOf(item);

  return (
    roiList.find((roi) => {
      const sameIp = String(roi.cameraIp || "").trim() === String(cameraIp || "").trim();
      const sameChannel = String(roi.channel || "") === channel;
      if (!sameIp || !sameChannel) return false;

      // Same IP can exist in different restaurants, so when both sides
      // carry a store name they must match as well.
      const roiStore = roi.storeName || roi.store || roi.restaurantName || roi.restaurant;
      if (roiStore && itemStore !== NO_STORE && !isAllStoresName(roiStore)) {
        return String(roiStore).trim() === itemStore;
      }
      return true;
    }) || null
  );
};

const itemTitle = (it, i) => `${it.camera_id || `Image ${i + 1}`}${it.type ? ` · ${it.type}` : ""}`;
const itemTime = (it) => {
  const d = new Date(it.updatedAt);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const HANDLES = {
  nw: "left-0 top-0 cursor-nwse-resize",
  ne: "left-full top-0 cursor-nesw-resize",
  sw: "left-0 top-full cursor-nesw-resize",
  se: "left-full top-full cursor-nwse-resize",
};

const ICONS = {
  draw: <><rect x="4" y="4" width="16" height="16" rx="2" strokeDasharray="3 3" /><path d="M12 9v6M9 12h6" /></>,
  move: <path d="M5 3l14 8-6 2-2 6L5 3z" />,
  polygon: <><path d="M12 3l8 6-3 11H7L4 9z" /><circle cx="12" cy="3" r="1" /><circle cx="20" cy="9" r="1" /><circle cx="17" cy="20" r="1" /><circle cx="7" cy="20" r="1" /><circle cx="4" cy="9" r="1" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  undo: <><path d="M9 14L4 9l5-5" /><path d="M4 9h10a6 6 0 010 12h-3" /></>,
  trash: <><path d="M4 7h16M10 11v6M14 11v6" /><path d="M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12M9 7V4h6v3" /></>,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  save: <><path d="M5 4h11l3 3v13H5z" /><path d="M8 4v5h7V4M8 20v-6h8v6" /></>,
  copy: <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" /></>,
  check: <path d="M5 12l5 5 9-10" />,
  image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M21 16l-5-5-9 9" /></>,
  fit: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  camera: <><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></>,
  chevron: <path d="M6 9l6 6 6-6" />,
  store: <><path d="M4 10l1.5-5h13L20 10" /><path d="M5 10v9h14v-9" /><path d="M4 10a2.7 2.7 0 005.3 0 2.7 2.7 0 005.4 0A2.7 2.7 0 0020 10" /></>,
};

const Icon = ({ name, className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

const IconButton = ({ icon, label, active, className = "", ...props }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent ${
      active ? "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50 hover:text-indigo-600" : ""
    } ${className}`}
    {...props}
  >
    <Icon name={icon} />
  </button>
);

/* ================================================================== */

export default function RoiPage() {
  // list of images from the API
  const [store, setStore] = useState("");
  const [items, setItems] = useState([]); // images of the CURRENT restaurant only (or the single store)
  const [restaurants, setRestaurants] = useState([]); // restaurant names (only used for "All Stores")
  const [itemsLoading, setItemsLoading] = useState(false); // loading images after switching restaurant
  const [itemsError, setItemsError] = useState("");
  const [roiData, setRoiData] = useState([]);
  const [roiLoading, setRoiLoading] = useState(false);
  const [listStatus, setListStatus] = useState("loading"); // loading | ready | error
  const [listError, setListError] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(""); // only used when store === "All Stores"
  const [selectedIp, setSelectedIp] = useState(""); // camera IP chosen in the dropdown
  const [savedIds, setSavedIds] = useState(() => new Set()); // saved during this session
  const [dirtyIds, setDirtyIds] = useState(() => new Set()); // images edited since their last save
  const [roiVersion, setRoiVersion] = useState(0); // bumps only when ROI is (re)fetched from the server

  // the image currently being edited
  const [image, setImage] = useState(null); // { src, name, width, height }
  const [status, setStatus] = useState("loading"); // loading | ready | error | empty
  const [error, setError] = useState("");
  const [regions, setRegions] = useState([]);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [tool, setTool] = useState("draw"); // draw | select | polygon
  const [zoom, setZoom] = useState(1);
  const [cursor, setCursor] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [saveError, setSaveError] = useState("");
  const [copied, setCopied] = useState(false);
  const [polyDraft, setPolyDraft] = useState([]); // points being placed (normalized)
  const [hover, setHover] = useState(null); // rubber-band end point

  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const stash = useRef({}); // unsaved regions per image id, so switching images never loses work
  const regionsRef = useRef(regions);
  regionsRef.current = regions;
  const polyRef = useRef([]);
  polyRef.current = polyDraft;
  const roiRef = useRef([]); // latest ROI list from the server (kept in sync after saves)
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;
  const restaurantReqRef = useRef(0); // guards against out-of-order restaurant responses

  const isAllStores = isAllStoresName(store);

  const activeItem = useMemo(() => items.find((i) => i._id === activeId) || null, [items, activeId]);
  const activeIndex = items.findIndex((i) => i._id === activeId);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [roiPassword, setRoiPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  /* ---------- restaurants (only relevant for "All Stores") ---------- */
  const restaurantOptions = useMemo(() => {
    if (!isAllStores) return [];
    return restaurants.map((name) => ({ name }));
  }, [restaurants, isAllStores]);

  // `items` already contains only the selected restaurant's images
  const itemsInStore = items;

  /* ---------- unique IPs + images for the selected IP ---------- */
  const ipOptions = useMemo(() => {
    const counts = new Map();
    itemsInStore.forEach((it) => {
      const ip = ipOf(it);
      counts.set(ip, (counts.get(ip) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([ip, count]) => ({ ip, count }))
      .sort((a, b) => a.ip.localeCompare(b.ip, undefined, { numeric: true }));
  }, [itemsInStore]);

  const visibleItems = useMemo(
    () => (selectedIp ? itemsInStore.filter((it) => ipOf(it) === selectedIp) : itemsInStore),
    [itemsInStore, selectedIp]
  );

  // put a freshly fetched list on screen: first IP + its first image
  const applyItems = useCallback((list) => {
    setItems(list);
    const firstIp = sortIps(list)[0] || "";
    const firstOfIp = list.find((it) => ipOf(it) === firstIp);
    setSelectedIp(firstIp);
    setActiveId(firstOfIp?._id ?? null);
  }, []);

  /* ---------- load the list ---------- */
  const loadList = useCallback(async () => {
    setListStatus("loading");
    setListError("");
    try {
      const storeName = localStorage.getItem(STORE_STORAGE_KEY) || "All Stores";

      if (isAllStoresName(storeName)) {
        // All Stores: get restaurant names, then fetch ONLY the first restaurant's images
        const names = await fetchRestaurants(storeName);
        setStore(storeName);
        setRestaurants(names);

        const first = names[0] || "";
        setSelectedRestaurant(first);

        if (first) {
          const { items: list } = await fetchImages(first);
          applyItems(list);
        } else {
          applyItems([]);
        }
      } else {
        // single store: fetch its images as before
        const { store: s, items: list } = await fetchImages(storeName);
        setStore(s);
        setRestaurants([]);
        setSelectedRestaurant("");
        applyItems(list);
      }

      setListStatus("ready");
    } catch (e) {
      setListError(e.message);
      setListStatus("error");
    }
  }, [applyItems]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ROI is loaded for the selected restaurant (All Stores) or the single store
  const roiStore = isAllStores ? selectedRestaurant : store;

  useEffect(() => {
    if (!roiStore) return;

    let cancelled = false;

    const loadRoi = async () => {
      try {
        setRoiLoading(true);

        const roi = await fetchEmpireRoi(roiStore);
        if (cancelled) return;

        roiRef.current = roi;
        setRoiData(roi);
        setRoiVersion((v) => v + 1);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load Empire ROI:", error);
        roiRef.current = [];
        setRoiData([]);
        setRoiVersion((v) => v + 1);
      } finally {
        if (!cancelled) setRoiLoading(false);
      }
    };

    loadRoi();

    return () => {
      cancelled = true;
    };
  }, [roiStore]);

  // Reads from a ref so saving doesn't re-trigger loadImage; roiVersion changes only on fetch.
  const getRoiForImage = useCallback(
    (item) => matchRoi(roiRef.current, item)?.regions || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roiVersion]
  );

  // images that already have ROI saved on the server
  const serverSavedIds = useMemo(
    () => new Set(items.filter((it) => matchRoi(roiData, it)?.regions?.length).map((it) => it._id)),
    [items, roiData]
  );

  /* ---------- load the active image ---------- */
  const loadImage = useCallback(() => {
    setDraft(null);
    setPolyDraft([]);
    setHover(null);
    setHistory([]);
    setSelectedId(null);
    setSaveState("idle");
    setSaveError("");
    setZoom(1);
    setError("");
    setImage(null);

    if (!activeItem) {
      setRegions([]);
      setStatus("empty");
      return () => {};
    }

    let cancelled = false;

    setStatus("loading");

    const img = new Image();

    img.onload = () => {
      if (cancelled) return;

      const W = img.naturalWidth;
      const H = img.naturalHeight;

      setImage({
        src: activeItem.image_url,
        name: itemTitle(activeItem, activeIndex),
        width: W,
        height: H,
      });

      if (stash.current[activeItem._id]) {
        // Use unsaved local ROI first
        setRegions(stash.current[activeItem._id]);
      } else {
        // Get ROI for this specific image/camera
        const existing = getRoiForImage(activeItem);

        setRegions(
          existing.map((r, i) => {
            const common = {
              id: uid(),
              label: r.label || `Region ${i + 1}`,
              color: COLORS[i % COLORS.length],
            };

            // Polygon: points are stored in pixels -> convert to normalized
            if (r.type === "polygon" && Array.isArray(r.points) && r.points.length >= 3) {
              return {
                ...common,
                type: "polygon",
                ...withBBox(
                  r.points.map((pt) => ({
                    x: Number(pt.x || 0) / W,
                    y: Number(pt.y || 0) / H,
                  }))
                ),
              };
            }

            // Rectangle (also covers old ROIs that have no `type`)
            // ROI is stored in pixels -> convert pixels to normalized coordinates
            return {
              ...common,
              type: "rect",
              x: Number(r.x || 0) / W,
              y: Number(r.y || 0) / H,
              w: Number(r.width || 0) / W,
              h: Number(r.height || 0) / H,
            };
          })
        );
      }

      setStatus("ready");
    };

    img.onerror = () => {
      if (cancelled) return;

      setError("The image file could not be displayed.");
      setStatus("error");
    };

    img.src = activeItem.image_url;

    return () => {
      cancelled = true;
    };

    // getRoiForImage is intentionally included here
  }, [activeItem, getRoiForImage]);

  useEffect(() => loadImage(), [loadImage]);

  const stashCurrent = () => {
    if (activeId && status === "ready" && dirtyIds.has(activeId)) {
      stash.current[activeId] = regionsRef.current;
    }
  };

  const switchImage = (id) => {
    if (id === activeId) return;
    stashCurrent();
    setActiveId(id);
  };

  // choosing a restaurant FETCHES only that restaurant's images, then opens the first IP / image
  const switchRestaurant = async (name) => {
    if (name === selectedRestaurant) return;
    stashCurrent();

    const reqId = ++restaurantReqRef.current;
    setSelectedRestaurant(name);
    setItemsLoading(true);
    setItemsError("");
    // clear the old restaurant's images right away so they never show under the new name
    setItems([]);
    setSelectedIp("");
    setActiveId(null);

    try {
      const { items: list } = await fetchImages(name);
      if (reqId !== restaurantReqRef.current) return; // a newer selection took over
      applyItems(list);
    } catch (e) {
      if (reqId !== restaurantReqRef.current) return;
      setItemsError(e.message);
      applyItems([]);
    } finally {
      if (reqId === restaurantReqRef.current) setItemsLoading(false);
    }
  };

  // choosing an IP shows only that IP's images (from the selected restaurant) and opens its first one
  const switchIp = (ip) => {
    if (ip === selectedIp) return;
    stashCurrent();
    setSelectedIp(ip);
    const first = itemsInStore.find((it) => ipOf(it) === ip);
    setActiveId(first?._id ?? null);
  };

  /* ---------- history / mutations ---------- */
  const touch = () => {
    setSaveState("idle");
    setSaveError("");
    const id = activeIdRef.current;
    if (id) setDirtyIds((s) => (s.has(id) ? s : new Set(s).add(id)));
  };
  const pushHistory = () => setHistory((h) => [...h.slice(-29), regionsRef.current]);

  const undo = useCallback(() => {
    if (!history.length) return;
    setRegions(history[history.length - 1]);
    setHistory(history.slice(0, -1));
    setSelectedId(null);
    touch();
  }, [history]);

  const removeRegion = useCallback((id) => {
    pushHistory();
    setRegions((rs) => rs.filter((r) => r.id !== id));
    setSelectedId((s) => (s === id ? null : s));
    touch();
  }, []);

  const clearAll = () => {
    pushHistory();
    setRegions([]);
    setSelectedId(null);
    touch();
  };

  // close the polygon that is currently being drawn
  const finishPolygon = useCallback(() => {
    const pts = polyRef.current;
    if (pts.length < 3) return;
    pushHistory();
    const id = uid();
    setRegions((prev) => [
      ...prev,
      {
        id,
        type: "polygon",
        label: `Region ${prev.length + 1}`,
        color: COLORS[prev.length % COLORS.length],
        ...withBBox(pts),
      },
    ]);
    setSelectedId(id);
    setPolyDraft([]);
    setHover(null);
    touch();
  }, []);

  // leaving the polygon tool cancels any half-drawn polygon
  useEffect(() => {
    if (tool !== "polygon") {
      setPolyDraft([]);
      setHover(null);
    }
  }, [tool]);

  const patchRegion = (id, patch) => {
    setRegions((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    touch();
  };

  const setPixel = (r, key, raw) => {
    const n = Number(raw);
    if (raw === "" || Number.isNaN(n)) return;
    const dim = key === "x" || key === "w" ? image.width : image.height;
    let v = clamp(n / dim);
    if (key === "x") v = Math.min(v, 1 - r.w);
    if (key === "y") v = Math.min(v, 1 - r.h);
    if (key === "w") v = Math.max(MIN_SIZE, Math.min(v, 1 - r.x));
    if (key === "h") v = Math.max(MIN_SIZE, Math.min(v, 1 - r.y));
    patchRegion(r.id, { [key]: v });
  };

  /* ---------- export ---------- */
  const toPixels = (r) => {
    const base = {
      label: r.label,
      type: r.type || "rect",
      x: Math.round(r.x * image.width),
      y: Math.round(r.y * image.height),
      width: Math.round(r.w * image.width),
      height: Math.round(r.h * image.height),
    };
    if (r.type === "polygon") {
      base.points = r.points.map((p) => ({
        x: Math.round(p.x * image.width),
        y: Math.round(p.y * image.height),
      }));
    }
    return base;
  };

  const payload = () => {
    // when "All Stores" is selected, save against the selected restaurant
    const itemStore = storeOf(activeItem);
    const saveStore = isAllStores
      ? itemStore !== NO_STORE
        ? itemStore
        : selectedRestaurant
      : store;

    return {
      store: saveStore,
      image: image.name,
      image_url: activeItem.image_url,
      camera_id: activeItem.camera_id,
      camera_ip: activeItem.cameraIp,
      type: activeItem.type,
      width: image.width,
      height: image.height,
      regions: regions.map(toPixels),
    };
  };

  const handleSave = useCallback(() => {
    if (!activeItem || !image || !regions.length) return;

    setRoiPassword("");
    setPasswordError("");
    setShowPasswordModal(true);
  }, [activeItem, image, regions]);

  const confirmSave = useCallback(async () => {
    if (!roiPassword.trim()) {
      setPasswordError("Please enter the password.");
      return;
    }

    if (roiPassword !== "citriot@2026") {
      setPasswordError("Incorrect password.");
      return;
    }

    setSaveState("saving");
    setSaveError("");
    setPasswordError("");

    try {
      const res = await saveRoi(
        activeItem._id,
        {
          ...payload(),
          password: roiPassword,
        }
      );

      delete stash.current[activeItem._id];

      setSavedIds((s) => new Set(s).add(activeItem._id));

      setDirtyIds((s) => {
        const n = new Set(s);
        n.delete(activeItem._id);
        return n;
      });

      const saved = res?.data;

      if (saved?._id) {
        const next = roiRef.current.some((r) => r._id === saved._id)
          ? roiRef.current.map((r) =>
              r._id === saved._id ? saved : r
            )
          : [saved, ...roiRef.current];

        roiRef.current = next;
        setRoiData(next);
      }

      setShowPasswordModal(false);
      setRoiPassword("");
      setSaveState("saved");
    } catch (e) {
      setSaveError(e.message);
      setSaveState("error");
    }
  }, [
    activeItem,
    image,
    regions,
    store,
    selectedRestaurant,
    isAllStores,
    roiPassword,
  ]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload(), null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  /* ---------- keyboard shortcuts ---------- */
  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName);
      const mod = e.metaKey || e.ctrlKey;

      // while a polygon is being drawn
      if (!typing && polyRef.current.length) {
        if (e.key === "Enter") {
          e.preventDefault();
          finishPolygon();
          return;
        }
        if (e.key === "Escape") {
          setPolyDraft([]);
          setHover(null);
          return;
        }
        if (e.key === "Backspace" || e.key === "Delete") {
          e.preventDefault();
          setPolyDraft((p) => p.slice(0, -1));
          return;
        }
      }

      if (mod && e.key.toLowerCase() === "z" && !typing) {
        e.preventDefault();
        undo();
      } else if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      } else if (!typing && (e.key === "Delete" || e.key === "Backspace") && selectedId) {
        removeRegion(selectedId);
      } else if (!typing && e.key === "Escape") {
        setSelectedId(null);
      } else if (!typing && e.key.toLowerCase() === "v") {
        setTool("select");
      } else if (!typing && e.key.toLowerCase() === "d") {
        setTool("draw");
      } else if (!typing && e.key.toLowerCase() === "p") {
        setTool("polygon");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, removeRegion, handleSave, finishPolygon, selectedId]);

  /* ---------- pointer interactions ---------- */
  const point = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };

  const startDrag = (e, drag) => {
    stageRef.current.setPointerCapture(e.pointerId);
    dragRef.current = { ...drag, start: point(e) };
  };

  const onStageDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    setSelectedId(null);

    if (tool === "polygon") {
      const p = point(e);
      const pts = polyRef.current;
      const rect = stageRef.current.getBoundingClientRect();
      const near = (a) => Math.hypot((a.x - p.x) * rect.width, (a.y - p.y) * rect.height) < 10;

      if (pts.length >= 3 && near(pts[0])) return finishPolygon(); // click first point to close
      if (pts.length && near(pts[pts.length - 1])) return; // ignore duplicate (double-click)
      setPolyDraft([...pts, p]);
      return;
    }

    if (tool === "draw") {
      const p = point(e);
      startDrag(e, { mode: "draw" });
      setDraft({ x: p.x, y: p.y, w: 0, h: 0 });
    }
  };

  const onRegionDown = (e, r) => {
    e.stopPropagation();
    setSelectedId(r.id);
    pushHistory();
    startDrag(e, { mode: "move", id: r.id, orig: r });
  };

  const onHandleDown = (e, r, handle) => {
    e.stopPropagation();
    pushHistory();
    startDrag(e, { mode: "resize", id: r.id, orig: r, handle });
  };

  const onVertexDown = (e, r, idx) => {
    e.stopPropagation();
    pushHistory();
    startDrag(e, { mode: "vertex", id: r.id, idx, orig: r });
  };

  const onMove = (e) => {
    const p = point(e);
    setCursor({ x: Math.round(p.x * image.width), y: Math.round(p.y * image.height) });

    if (tool === "polygon") {
      if (polyRef.current.length) setHover(p);
      return;
    }

    const d = dragRef.current;
    if (!d) return;

    if (d.mode === "draw") {
      setDraft({ x: Math.min(d.start.x, p.x), y: Math.min(d.start.y, p.y), w: Math.abs(p.x - d.start.x), h: Math.abs(p.y - d.start.y) });
    } else if (d.mode === "move") {
      const o = d.orig;
      const nx = clamp(o.x + p.x - d.start.x, 0, 1 - o.w);
      const ny = clamp(o.y + p.y - d.start.y, 0, 1 - o.h);
      if (o.type === "polygon") {
        const dx = nx - o.x;
        const dy = ny - o.y;
        patchRegion(d.id, {
          x: nx,
          y: ny,
          points: o.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy })),
        });
      } else {
        patchRegion(d.id, { x: nx, y: ny });
      }
    } else if (d.mode === "vertex") {
      const pts = d.orig.points.map((pt, i) => (i === d.idx ? p : pt));
      patchRegion(d.id, withBBox(pts));
    } else if (d.mode === "resize") {
      const o = d.orig;
      const fixedX = d.handle.includes("w") ? o.x + o.w : o.x;
      const fixedY = d.handle.includes("n") ? o.y + o.h : o.y;
      patchRegion(d.id, {
        x: Math.min(fixedX, p.x),
        y: Math.min(fixedY, p.y),
        w: Math.max(MIN_SIZE, Math.abs(p.x - fixedX)),
        h: Math.max(MIN_SIZE, Math.abs(p.y - fixedY)),
      });
    }
  };

  const onUp = () => {
    const d = dragRef.current;
    if (d?.mode === "draw" && draft && draft.w > MIN_SIZE && draft.h > MIN_SIZE) {
      pushHistory();
      const id = uid();
      setRegions((prev) => [
        ...prev,
        { id, type: "rect", label: `Region ${prev.length + 1}`, color: COLORS[prev.length % COLORS.length], ...draft },
      ]);
      setSelectedId(id);
      touch();
    }
    dragRef.current = null;
    setDraft(null);
  };

  /* ---------- derived ---------- */
  const boxStyle = (r) => ({ left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` });
  const coverage = Math.min(100, Math.round(regions.reduce((sum, r) => sum + regionArea(r), 0) * 100));
  const aspect = image ? image.width / image.height : 1;
  const saveLabel = { idle: "Save regions", saving: "Saving…", saved: "Saved", error: "Try again" }[saveState];
  const isSaved = (id) => savedIds.has(id) || serverSavedIds.has(id);
  const savedInIp = visibleItems.filter((it) => isSaved(it._id)).length;

  /* ================================================================ */
  if (listStatus === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-500">
        Loading images…
      </div>
    );
  }

  if (listStatus === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-sm rounded-2xl border border-rose-100 bg-white p-8 text-center shadow-sm">
          <p className="font-medium text-slate-900">Couldn’t load images</p>
          <p className="mt-1 text-sm text-slate-500">{listError}</p>
          <button
            onClick={loadList}
            className="mt-5 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // For "All Stores" we keep the page (and its dropdowns) even when a restaurant has no images
  if (!items.length && !isAllStores) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center">
        <div>
          <p className="font-medium text-slate-900">No images for {store || "this store"}</p>
          <p className="mt-1 text-sm text-slate-500">Select a different store, or check back once cameras have uploaded images.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-800">
      <div className="relative mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        {/* Title */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-[28px]">Mark regions of interest</h1>
            <p className="mt-1 text-sm text-slate-500">
              {store ? `${store} · ` : ""}
              {isAllStores
                ? "Choose a restaurant, then a camera IP, pick an image, draw boxes around the areas that matter, then save."
                : "Choose a camera IP, pick an image, draw boxes around the areas that matter, then save."}
            </p>
          </div>
          {image && (
            <div className="flex max-w-full items-center gap-2 self-start rounded-full border border-slate-200 bg-white/80 py-1.5 pl-2 pr-4 text-sm shadow-sm backdrop-blur sm:self-auto">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                <Icon name="image" className="h-4 w-4" />
              </span>
              <span className="truncate font-medium text-slate-700">{image.name}</span>
              <span className="shrink-0 text-slate-400">{image.width}×{image.height}</span>
            </div>
          )}
        </div>

        {/* Restaurant + Camera IP dropdowns */}
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          {/* Restaurant dropdown — only when "All Stores" is selected */}
          {isAllStores && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label htmlFor="restaurant-select" className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                  <Icon name="store" className="h-4 w-4" />
                </span>
                Restaurant
              </label>
              <div className="relative">
                <select
                  id="restaurant-select"
                  value={selectedRestaurant}
                  onChange={(e) => switchRestaurant(e.target.value)}
                  disabled={itemsLoading}
                  className="h-10 min-w-[16rem] cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3.5 pr-10 text-sm font-medium text-slate-800 shadow-sm transition hover:border-slate-300 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:cursor-wait disabled:opacity-60"
                >
                  {restaurantOptions.map(({ name }) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          )}

          {/* Camera IP dropdown */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label htmlFor="ip-select" className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                <Icon name="camera" className="h-4 w-4" />
              </span>
              Camera IP
            </label>
            <div className="relative">
              <select
                id="ip-select"
                value={selectedIp}
                onChange={(e) => switchIp(e.target.value)}
                disabled={itemsLoading || !ipOptions.length}
                className="h-10 min-w-[16rem] cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3.5 pr-10 text-sm font-medium text-slate-800 shadow-sm transition hover:border-slate-300 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {ipOptions.map(({ ip, count }) => (
                  <option key={ip} value={ip}>
                    {ip} ({count} {count === 1 ? "image" : "images"})
                  </option>
                ))}
              </select>
              <Icon name="chevron" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <span className="text-xs text-slate-500">
            {itemsLoading
              ? "Loading images…"
              : `${ipOptions.length} ${ipOptions.length === 1 ? "camera" : "cameras"} · ${savedInIp}/${visibleItems.length} saved for this IP`}
          </span>
        </div>

        {itemsError && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
            <span>{itemsError}</span>
            <button
              type="button"
              onClick={() => {
                const name = selectedRestaurant;
                setSelectedRestaurant("");
                switchRestaurant(name);
              }}
              className="shrink-0 font-medium underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Image picker (only images of the selected IP) */}
        <div
          className="mb-5 flex min-h-[2px] gap-3 overflow-x-auto pb-1"
          role="listbox"
          aria-label={`Images for ${isAllStores && selectedRestaurant ? `${selectedRestaurant} · ` : ""}${selectedIp}`}
        >
          {visibleItems.map((it) => {
            const active = it._id === activeId;
            const pending = dirtyIds.has(it._id); // edited since last save (or never saved)
            const done = !pending && isSaved(it._id);
            return (
              <button
                key={it._id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => switchImage(it._id)}
                className={`group relative w-40 shrink-0 overflow-hidden rounded-xl border bg-white text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  active ? "border-indigo-400 ring-2 ring-indigo-100 shadow-md" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                }`}
              >
                <div className="aspect-[16/10] w-full bg-slate-100">
                  <img src={it.image_url} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" />
                </div>
                <div className="px-2.5 py-2">
                  <p className="truncate text-xs font-medium text-slate-800">{itemTitle(it, items.indexOf(it))}</p>
                  <p className="truncate text-[11px] text-slate-400">{itemTime(it) || it.camera_ip}</p>
                </div>
                {(done || pending) && (
                  <span
                    className={`absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium shadow-sm ${
                      done ? "bg-emerald-500 text-white" : "bg-amber-400 text-amber-950"
                    }`}
                  >
                    {done ? <Icon name="check" className="h-3 w-3" /> : null}
                    {done ? "Saved" : "Unsaved"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* ================= Canvas ================= */}
          <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(15,23,42,0.06)]">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
              <div className="flex items-center gap-1 rounded-xl bg-slate-50 p-1">
                <IconButton icon="draw" label="Rectangle (D)" active={tool === "draw"} onClick={() => setTool("draw")} />
                <IconButton icon="polygon" label="Polygon (P)" active={tool === "polygon"} onClick={() => setTool("polygon")} />
                <IconButton icon="move" label="Select & edit (V)" active={tool === "select"} onClick={() => setTool("select")} />
              </div>

              <div className="flex items-center gap-1">
                <IconButton icon="undo" label="Undo (Ctrl+Z)" onClick={undo} disabled={!history.length} />
                <div className="mx-1 h-5 w-px bg-slate-200" />
                <IconButton icon="minus" label="Zoom out" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - 0.25).toFixed(2)))} disabled={zoom <= ZOOM_MIN} />
                <button
                  type="button"
                  onClick={() => setZoom(1)}
                  title="Reset zoom"
                  className="h-9 min-w-[3.5rem] rounded-lg px-2 text-sm font-medium tabular-nums text-slate-600 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <IconButton icon="plus" label="Zoom in" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + 0.25).toFixed(2)))} disabled={zoom >= ZOOM_MAX} />
                <IconButton icon="fit" label="Fit to view" onClick={() => setZoom(1)} />
              </div>
            </div>

            {/* Viewport */}
            <div
              className="flex h-[60vh] min-h-[380px] overflow-auto p-6 sm:p-8"
              style={{
                backgroundColor: "#f8fafc",
                backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)",
                backgroundSize: "18px 18px",
              }}
            >
              {itemsLoading && (
                <div className="m-auto w-full max-w-2xl animate-pulse">
                  <div className="aspect-[4/3] rounded-xl bg-slate-200/80" />
                  <p className="mt-4 text-center text-sm text-slate-500">
                    Loading {selectedRestaurant || "restaurant"} images…
                  </p>
                </div>
              )}

              {!itemsLoading && status === "empty" && (
                <div className="m-auto max-w-sm text-center">
                  <p className="font-medium text-slate-900">
                    No images for {selectedRestaurant || store || "this selection"}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Pick another restaurant or camera IP, or check back once cameras have uploaded images.
                  </p>
                </div>
              )}

              {!itemsLoading && status === "loading" && (
                <div className="m-auto w-full max-w-2xl animate-pulse">
                  <div className="aspect-[4/3] rounded-xl bg-slate-200/80" />
                  <p className="mt-4 text-center text-sm text-slate-500">Loading image…</p>
                </div>
              )}

              {!itemsLoading && status === "error" && (
                <div className="m-auto max-w-sm rounded-2xl border border-rose-100 bg-white p-8 text-center shadow-sm">
                  <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                    <Icon name="close" className="h-5 w-5" />
                  </div>
                  <p className="font-medium text-slate-900">The image didn’t load</p>
                  <p className="mt-1 text-sm text-slate-500">{error}</p>
                  <button
                    onClick={loadImage}
                    className="mt-5 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                  >
                    Try again
                  </button>
                </div>
              )}

              {!itemsLoading && status === "ready" && image && (
                <div
                  ref={stageRef}
                  onPointerDown={onStageDown}
                  onPointerMove={onMove}
                  onPointerUp={onUp}
                  onPointerCancel={onUp}
                  onDoubleClick={tool === "polygon" ? finishPolygon : undefined}
                  onPointerLeave={() => {
                    if (!dragRef.current) setCursor(null);
                    setHover(null);
                  }}
                  style={{ width: `calc(min(100%, 50vh * ${aspect}) * ${zoom})` }}
                  className={`relative m-auto shrink-0 touch-none select-none rounded-lg shadow-[0_10px_40px_rgb(15,23,42,0.18)] ring-1 ring-slate-900/10 ${
                    tool === "select" ? "cursor-default" : "cursor-crosshair"
                  }`}
                >
                  <img src={image.src} alt={image.name} draggable={false} className="block w-full rounded-lg" />

                  {regions.map((r) => {
                    const active = r.id === selectedId;

                    /* ----- polygon region ----- */
                    if (r.type === "polygon") {
                      const interactive = tool === "select";
                      return (
                        <div key={r.id} className="pointer-events-none absolute inset-0">
                          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
                            <polygon
                              points={svgPoints(r.points)}
                              fill={r.color}
                              fillOpacity={active ? 0.2 : 0.12}
                              stroke={r.color}
                              strokeWidth={active ? 2.5 : 2}
                              strokeLinejoin="round"
                              vectorEffect="non-scaling-stroke"
                              style={{ pointerEvents: interactive ? "all" : "none", cursor: interactive ? "move" : "default" }}
                              onPointerDown={interactive ? (e) => onRegionDown(e, r) : undefined}
                            />
                          </svg>

                          <span
                            style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%`, backgroundColor: r.color }}
                            className="pointer-events-none absolute max-w-[12rem] -translate-y-full truncate rounded-t-md px-2 py-0.5 text-[11px] font-medium text-white shadow-sm"
                          >
                            {r.label}
                          </span>

                          {active && interactive &&
                            r.points.map((pt, i) => (
                              <span
                                key={i}
                                onPointerDown={(e) => onVertexDown(e, r, i)}
                                style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%`, borderColor: r.color }}
                                className="pointer-events-auto absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-move rounded-full border-2 bg-white shadow"
                              />
                            ))}
                        </div>
                      );
                    }

                    /* ----- rectangle region ----- */
                    return (
                      <div
                        key={r.id}
                        onPointerDown={tool === "select" ? (e) => onRegionDown(e, r) : undefined}
                        style={{
                          ...boxStyle(r),
                          borderColor: r.color,
                          backgroundColor: `${r.color}${active ? "33" : "1f"}`,
                          borderWidth: active ? 2.5 : 2,
                        }}
                        className={`absolute rounded-[3px] border-solid ${tool === "select" ? "cursor-move" : "pointer-events-none"}`}
                      >
                        <span
                          style={{ backgroundColor: r.color }}
                          className="pointer-events-none absolute -top-px left-[-2px] max-w-[12rem] -translate-y-full truncate rounded-t-md px-2 py-0.5 text-[11px] font-medium text-white shadow-sm"
                        >
                          {r.label}
                        </span>

                        {active && tool === "select" &&
                          Object.entries(HANDLES).map(([h, pos]) => (
                            <span
                              key={h}
                              onPointerDown={(e) => onHandleDown(e, r, h)}
                              style={{ borderColor: r.color }}
                              className={`absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white shadow ${pos}`}
                            />
                          ))}
                      </div>
                    );
                  })}

                  {draft && (
                    <div
                      style={boxStyle(draft)}
                      className="pointer-events-none absolute rounded-[3px] border-2 border-dashed border-indigo-500 bg-indigo-500/15"
                    />
                  )}

                  {/* polygon being drawn */}
                  {polyDraft.length > 0 && (
                    <div className="pointer-events-none absolute inset-0">
                      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
                        <polygon
                          points={svgPoints(hover ? [...polyDraft, hover] : polyDraft)}
                          fill="#6366f1"
                          fillOpacity="0.15"
                          stroke="#6366f1"
                          strokeWidth="2"
                          strokeDasharray="6 4"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                      {polyDraft.map((pt, i) => (
                        <span
                          key={i}
                          style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%` }}
                          className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-indigo-500 bg-white shadow ${
                            i === 0 && polyDraft.length >= 3 ? "h-4 w-4 ring-2 ring-indigo-300" : "h-2.5 w-2.5"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Status bar */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
              <span>
                {tool === "draw"
                  ? "Drag on the image to draw a region."
                  : tool === "polygon"
                  ? "Click to add points. Click the first point, double-click or press Enter to close. Backspace removes a point, Esc cancels."
                  : "Drag a region to move it, or pull a corner/point to resize."}
              </span>
              <span className="tabular-nums">
                {cursor ? `x ${cursor.x}  ·  y ${cursor.y}` : "x –  ·  y –"}
              </span>
            </div>
          </section>

          {/* ================= Regions panel ================= */}
          <aside className="flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(15,23,42,0.06)] lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:self-start">
            <div className="p-5 pb-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Regions</h2>
                {regions.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-sm font-medium text-slate-400 transition hover:text-rose-600 focus:outline-none focus-visible:underline"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-indigo-50/70 px-4 py-3">
                  <p className="text-2xl font-semibold tabular-nums text-indigo-700">{regions.length}</p>
                  <p className="text-xs text-indigo-600/80">Marked</p>
                </div>
                <div className="rounded-xl bg-sky-50/80 px-4 py-3">
                  <p className="text-2xl font-semibold tabular-nums text-sky-700">{coverage}%</p>
                  <p className="text-xs text-sky-600/80">Of image covered</p>
                </div>
              </div>
            </div>

            <div className="min-h-[160px] flex-1 space-y-2 overflow-y-auto border-t border-slate-100 bg-slate-50/50 p-3">
              {!image ? null : regions.length === 0 ? (
                <div className="flex min-h-[140px] flex-col items-center justify-center px-6 text-center">
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200">
                    <Icon name="draw" className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">No regions yet</p>
                  <p className="mt-0.5 text-xs text-slate-500">Draw on the image to add your first one.</p>
                </div>
              ) : (
                regions.map((r) => {
                  const p = toPixels(r);
                  const active = r.id === selectedId;
                  return (
                    <div
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      className={`cursor-pointer rounded-xl border bg-white p-3 transition ${
                        active ? "border-indigo-300 shadow-md ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: r.color }} />
                        <div className="min-w-0 flex-1">
                          <input
                            value={r.label}
                            onChange={(e) => patchRegion(r.id, { label: e.target.value })}
                            aria-label="Region name"
                            className="w-full rounded-md bg-transparent px-1.5 py-0.5 text-sm font-medium text-slate-800 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                          />
                          <p className="px-1.5 text-xs tabular-nums text-slate-400">
                            {p.type === "polygon" ? `Polygon · ${p.points.length} points` : `${p.width} × ${p.height} px`}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRegion(r.id);
                          }}
                          aria-label={`Delete ${r.label}`}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                        >
                          <Icon name="trash" />
                        </button>
                      </div>

                      {active && p.type !== "polygon" && (
                        <div className="mt-3 grid grid-cols-4 gap-2" onClick={(e) => e.stopPropagation()}>
                          {[["x", "X", p.x], ["y", "Y", p.y], ["w", "W", p.width], ["h", "H", p.height]].map(([key, lab, val]) => (
                            <label key={key} className="block">
                              <span className="mb-0.5 block text-[11px] font-medium text-slate-400">{lab}</span>
                              <input
                                type="number"
                                value={val}
                                onChange={(e) => setPixel(r, key, e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs tabular-nums text-slate-700 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t border-slate-100 p-4">
              {saveState === "error" && (
                <p className="mb-2 text-xs text-rose-600">{saveError || "Your regions weren’t saved. Check your connection and try again."}</p>
              )}
              {saveState === "saved" && (
                <p className="mb-2 text-xs text-emerald-600">Regions saved for {image?.name}.</p>
              )}
              <div className="grid grid-cols-[auto_1fr] gap-2">
                <button
                  onClick={copyJson}
                  disabled={!regions.length || !image}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-700"
                >
                  <Icon name={copied ? "check" : "copy"} />
                  {copied ? "Copied" : "JSON"}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!regions.length || !image || saveState === "saving"}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm shadow-indigo-600/30 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                >
                  <Icon name={saveState === "saved" ? "check" : "save"} />
                  {saveLabel}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Modal
        title="Confirm ROI Save"
        open={showPasswordModal}
        onCancel={() => {
          if (saveState === "saving") return;

          setShowPasswordModal(false);
          setRoiPassword("");
          setPasswordError("");
        }}
        onOk={confirmSave}
        okText={saveState === "saving" ? "Saving..." : "Confirm & Save"}
        cancelText="Cancel"
        confirmLoading={saveState === "saving"}
        okButtonProps={{
          disabled: !roiPassword.trim(),
        }}
        centered
      >
        <div className="py-2">
          <p className="mb-4 text-sm text-slate-500">
            Enter the password to save the ROI configuration.
          </p>

          <Input
            type="text"
            value={roiPassword}
            onChange={(e) => {
              setRoiPassword(e.target.value);
              setPasswordError("");
            }}
            onPressEnter={() => {
              if (roiPassword.trim() && saveState !== "saving") {
                confirmSave();
              }
            }}
            placeholder="Enter password"
            autoFocus
            size="large"
            autoComplete="off"
            name="roi-password-confirmation"
            spellCheck={false}
          />

          {passwordError && (
            <p className="mt-2 text-sm text-rose-600">
              {passwordError}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}